import { ActivatedRoute, Router } from '@angular/router';
import {
  INTEGRATIONS_ROUTE,
  INTERACTIONS_HISTORY_ROUTE,
  LEAD_LIFECYCLE_ROUTE,
  ORGANIZATION_ROUTE_PARAM,
  RULES_ROUTE,
  SUPERVISORS_ROUTE,
} from '../../app.interface';

/**
 * Detail routes whose URL carries an org-specific entity id (a supervisor, rule,
 * interaction, …). When the org changes that id no longer resolves in the new org,
 * so swapping the org segment in place would point at a non-existent entity and
 * break navigation. Each entry tests the current path segments; on a match, the org
 * switch redirects to `safeBase` (an org-scoped list/base) instead.
 */
const ENTITY_DETAIL_REDIRECTS: { matches: (segments: string[]) => boolean; safeBase: string }[] = [
  {
    // Supervisor view: /supervisor/:organization/:supervisorId(/tab)
    // Supervisor edit: /supervisors/:organization/edit/:id
    matches: (s) => s[0] === 'supervisor' || (s[0] === 'supervisors' && s.includes('edit')),
    safeBase: SUPERVISORS_ROUTE.BASE_FULL_PATH,
  },
  {
    // Rule edit: /rules/:organization/edit/:id
    matches: (s) => s[0] === 'rules' && s.includes('edit'),
    safeBase: RULES_ROUTE.BASE_FULL_PATH,
  },
  {
    // Interaction transcript: /interaction/:organization/:id
    matches: (s) => s[0] === 'interaction',
    safeBase: INTERACTIONS_HISTORY_ROUTE.BASE_FULL_PATH,
  },
  {
    // Lead lifecycle config edit: /lead-lifecycle/:organization/configuration/edit/:id
    matches: (s) => s[0] === 'lead-lifecycle' && s.includes('configuration') && s.includes('edit'),
    safeBase: LEAD_LIFECYCLE_ROUTE.CONFIGURATION_FULL_PATH,
  },
  {
    // Integration edit: /integrations/:organization/edit/:integrationId
    matches: (s) => s[0] === 'integrations' && s.includes('edit'),
    safeBase: INTEGRATIONS_ROUTE.BASE_FULL_PATH,
  },
];

/**
 * Splits a path template (e.g. `/supervisors/:organization`) into navigation
 * segments, filling :organization with the given org.
 */
function templateToSegments(template: string, organization: string): string[] {
  return template
    .split('/')
    .filter(Boolean)
    .map((segment) => (segment === `:${ORGANIZATION_ROUTE_PARAM}` ? organization : segment));
}

/**
 * Computes the navigation segments for switching org from `currentOrg` to
 * `newOrgName` given the current URL `segments`. For an entity-detail route whose
 * id won't exist in the new org, returns the section's safe base; otherwise swaps
 * the org segment in place. Returns null when there is nothing to navigate to.
 */
export function resolveOrgSwitchSegments(segments: string[], currentOrg: string, newOrgName: string): string[] | null {
  const redirect = ENTITY_DETAIL_REDIRECTS.find((r) => r.matches(segments));
  if (redirect) {
    return templateToSegments(redirect.safeBase, newOrgName);
  }
  const idx = segments.indexOf(currentOrg);
  if (idx === -1) {
    return null;
  }
  const newSegments = [...segments];
  newSegments[idx] = newOrgName;
  return newSegments;
}

/**
 * Builds a path by replacing :organization and optional placeholders (e.g. :id) in a route template.
 * If organization is null/undefined, returns '/' as a safe fallback to avoid navigating to literal :organization.
 */
export function buildPathWithOrganization(
  pathTemplate: string,
  organization: string | null | undefined,
  extraParams?: Record<string, string>,
): string {
  if (organization == null || organization === '') {
    return '/';
  }
  let path = pathTemplate.replace(`:${ORGANIZATION_ROUTE_PARAM}`, organization);
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      path = path.replace(`:${key}`, value);
    }
  }
  return path;
}

/**
 * Reads the :organization route param from the current router state.
 * Walks the whole activated-route chain (root → leaf) and returns the param wherever
 * it appears. Reading only the leaf misses it on nested routes (e.g. the supervisor
 * view's child tabs), whose leaf route carries no :organization of its own.
 */
export function getOrganizationFromRouter(router: Router): string | null {
  let route: ActivatedRoute | null = router.routerState.root;
  let organization: string | null = null;
  while (route) {
    const value = route.snapshot.paramMap.get(ORGANIZATION_ROUTE_PARAM);
    if (value != null) {
      organization = value;
    }
    route = route.firstChild;
  }
  return organization;
}

/**
 * Navigates to reflect an organization switch to newOrgName. Swaps the organization
 * segment in place for most routes, but redirects entity-detail routes (e.g. a
 * supervisor view/edit) to their section's safe base, since the entity id in the URL
 * does not exist in the new org. No-op if the current route has no organization param
 * or it already equals newOrgName.
 */
export function syncOrganizationParamToUrl(router: Router, newOrgName: string): void {
  const currentOrg = getOrganizationFromRouter(router);
  if (currentOrg === null || currentOrg === newOrgName) {
    return;
  }
  let route = router.routerState.root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  const segments = route.snapshot.pathFromRoot.flatMap((r) => r.url.map((u) => u.path));
  const newSegments = resolveOrgSwitchSegments(segments, currentOrg, newOrgName);
  if (newSegments === null) {
    return;
  }
  void router.navigate(newSegments, { queryParamsHandling: 'preserve', replaceUrl: true });
}
